import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, User, Tag } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/posts/${slug}`)
      .then(r => setPost(r.data))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-[#0D0D0D] min-h-screen flex items-center justify-center pt-20">
        <div className="text-[#B0B0B0]">Loading...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-[#0D0D0D] min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#F5F5F7] mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>Post Not Found</h1>
          <Link to="/blog" className="text-[#38b6ff] hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0D0D0D] pt-16 lg:pt-20 min-h-screen" data-testid="blog-post-page">
      <article className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back link */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-[#B0B0B0] hover:text-[#38b6ff] transition-colors mb-8"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
            data-testid="blog-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>

          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs px-2 py-1 border rounded text-[#38b6ff] bg-[#38b6ff]/10 border-[#38b6ff]/30">
                <Tag className="w-3 h-3 inline mr-1" />{post.category}
              </span>
              <span className="text-xs text-[#B0B0B0] flex items-center gap-1">
                <Calendar className="w-3 h-3" />{new Date(post.created_at).toLocaleDateString()}
              </span>
            </div>

            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#F5F5F7] mb-6"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="blog-post-title"
            >
              {post.title}
            </h1>

            <div className="flex items-center gap-2 mb-10 pb-8 border-b border-white/10">
              <User className="w-4 h-4 text-[#38b6ff]" />
              <span className="text-sm text-[#E0E0E0]">{post.author}</span>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="prose-blog"
            data-testid="blog-post-content"
          >
            {post.content.split('\n').map((block, i) => {
              if (block.startsWith('## ')) {
                return <h2 key={i} className="text-xl font-bold text-[#F5F5F7] mt-10 mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>{block.replace('## ', '')}</h2>;
              }
              if (block.startsWith('**') && block.endsWith('**')) {
                return <p key={i} className="text-[#F5F5F7] font-semibold mt-4 mb-2">{block.replace(/\*\*/g, '')}</p>;
              }
              if (block.startsWith('- ')) {
                return <li key={i} className="text-[#E0E0E0] text-sm leading-relaxed ml-4 mb-1 list-disc">{block.replace('- ', '')}</li>;
              }
              if (block.trim() === '') return <div key={i} className="h-4" />;
              return <p key={i} className="text-[#E0E0E0] leading-relaxed mb-4">{block}</p>;
            })}
          </motion.div>

          {/* Bottom CTA */}
          <div className="mt-16 pt-8 border-t border-white/10 text-center">
            <p className="text-[#B0B0B0] mb-4">Interested in becoming a court reporter?</p>
            <Link
              to="/stenou#consultation"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#38b6ff] text-white font-semibold hover:bg-[#38b6ff]/90 transition-colors"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
              data-testid="blog-post-cta"
            >
              Book a Free Consultation
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
